import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'

import { WorklogService, worklogErrorMessage } from '../../data/services/worklog.service'
import { AREA_META, STATUS_META, TYPE_META } from '../../data/constants/worklog.constants'
import { EpicTask, EpicWithStatus } from '../../data/interfaces/epic.interface'
import { EPIC_CATEGORIES, EPIC_STATUS_META } from '@modules/private/roadmap/data/constants/roadmap.constants'
import { motivoNoRetirarEpica, puedeRetirarEpica } from '../../data/constants/epic-actions'

const MS_HOUR = 3600000
const MS_DAY = 86400000

/** Mismo redondeo que el Gantt: horas por debajo de dos días, días por encima. */
function durLabel(ms: number | null): string | null {
  if (ms == null || ms < 0) return null
  if (ms < MS_HOUR) return `${Math.max(Math.round(ms / 60000), 1)} min`
  if (ms < 2 * MS_DAY) {
    const h = Math.floor(ms / MS_HOUR)
    const m = Math.round((ms % MS_HOUR) / 60000)
    return m ? `${h} h ${m} m` : `${h} h`
  }
  return `${Math.round(ms / MS_DAY)} d`
}

/**
 * Fila de tarea dentro de la épica, ya resuelta para pintarse.
 *
 * `EpicTask.areas` y `EpicTask.status` llegan como STRING —el backend no los tipa contra los
 * catálogos— así que la búsqueda en `AREA_META`/`STATUS_META` se hace aquí y no en la plantilla,
 * donde `strictTemplates` no acepta indexar un `Record` con una cadena suelta.
 */
interface FilaTarea {
  task: EpicTask
  duracion: string
  medida: boolean
  color: string
  icon: string
  estado: string
  estadoColor: string
  areas: { label: string; color: string }[]
}

/**
 * Ficha de una ÉPICA. Deliberadamente distinta de la de una tarea: una épica no se asigna, no se
 * mueve de columna y no tiene comentarios — es un agrupador, y lo que hay que poder contestar
 * frente a ella es *de qué se compone* y *cuánto ha tardado de verdad*.
 *
 * De ahí que el bloque grande sea el reloj y no la descripción. La barra del Gantt codifica dos
 * números que no se explican en ningún otro sitio de la aplicación —el trabajo (la unión de los
 * tramos de sus tareas) y la envolvente (de la primera a la última, huecos incluidos)— y la
 * diferencia entre los dos son los días que la épica estuvo abierta sin que nadie la tocara. Ese
 * hueco es la señal, y hasta ahora solo se intuía mirando los espacios entre los tramos.
 *
 * El estado NO se edita aquí: lo deriva el backend del trabajo real (ADR-0040 · D13). Lo que sí se
 * corrige (F-037) son las dos cosas que una épica puede tener mal desde que se creó: **el nombre**,
 * y **su propia existencia** si se registró de más. Las dos son del equipo (`isDev`) y las dos ya
 * existían en el backend sin que ninguna pantalla las llamara.
 */
@Component({
  selector: 'app-worklog-epic-detail',
  templateUrl: './worklog-epic-detail.component.html',
  styleUrls: ['./worklog-epic-detail.component.scss'],
})
export class WorklogEpicDetailComponent implements OnChanges {
  @Input() visible = false
  @Output() visibleChange = new EventEmitter<boolean>()

  @Input() row: EpicWithStatus | null = null

  /** Abrir una tarea desde aquí reutiliza el diálogo de tarea del contenedor. */
  @Output() openItem = new EventEmitter<string>()

  //* Renombrar y retirar son del equipo. El backend lo exige igual (`assertDev`), así que esto no
  //* es la puerta: es no ofrecer un botón que va a rebotar.
  @Input() isDev = false

  /** El catálogo cambió: quien contiene esta ficha vuelve a pedir las épicas. */
  @Output() changed = new EventEmitter<void>()

  tasks: FilaTarea[] | null = null
  loading = false

  //* Renombrado en sitio y no en un diálogo aparte: es una corrección de una línea, y un segundo
  //* modal encima de este taparía justo el título que se está editando.
  editandoTitulo = false
  tituloBorrador = ''
  confirmandoRetiro = false
  guardando = false
  errorMsg: string | null = null

  readonly EPIC_STATUS_META = EPIC_STATUS_META

  constructor(private worklog: WorklogService) {}

  ngOnChanges(): void {
    //* Al cambiar de épica —o al cerrar— se descarta cualquier edición a medias: dejar el borrador
    //* vivo haría que la ficha siguiente se abriera con el título de la anterior en la caja.
    this.editandoTitulo = false
    this.confirmandoRetiro = false
    this.errorMsg = null

    //* Las tareas se piden al abrir y no al construir: la ficha se abre para una épica de treinta,
    //* y precargarlas todas serían treinta consultas para mirar una.
    if (!this.visible || !this.row) return
    const slug = this.row.epic.slug
    this.tasks = null
    this.loading = true
    this.worklog.getEpicTasks(slug).subscribe({
      next: (ts) => {
        this.tasks = ts.map((t) => this.aFila(t))
        this.loading = false
      },
      error: () => {
        this.tasks = []
        this.loading = false
      },
    })
  }

  private aFila(t: EpicTask): FilaTarea {
    //* El reloj de la tarea arranca al salir de Backlog y se detiene al entrar a Deploy
    //* (ADR-0048 · D2). Sin cualquiera de los dos extremos no hay duración que afirmar.
    const ini = t.startedAt ? new Date(t.startedAt).getTime() : null
    const fin = t.releasedAt ? new Date(t.releasedAt).getTime() : t.deliveredAt ? new Date(t.deliveredAt).getTime() : null
    //* Sin recorrido observado no hay duración que afirmar, aunque las dos fechas existan.
    const ms = t.observed !== false && ini != null && fin != null ? fin - ini : null
    const meta = (TYPE_META as any)[t.type]
    const est = (STATUS_META as any)[t.status]
    return {
      task: t,
      duracion: durLabel(ms) ?? (t.observed === false ? 'sin recorrido' : 'sin medir'),
      medida: ms != null,
      color: meta?.color ?? '#94a3b8',
      icon: meta?.icon ?? 'fa-solid fa-list-check',
      estado: est?.label ?? t.status,
      estadoColor: est?.color ?? '#94a3b8',
      areas: (t.areas ?? []).map((a) => {
        const am = (AREA_META as any)[a]
        return { label: am?.short ?? a, color: am?.color ?? '#94a3b8' }
      }),
    }
  }

  // ─────────── Lectura ───────────

  get category() {
    return EPIC_CATEGORIES.find((c) => c.id === this.row?.epic.category) ?? null
  }

  get accent(): string {
    return this.category?.color ?? '#8B5CF6'
  }

  get progress(): number {
    const r = this.row
    return r?.taskCount ? Math.round((r.doneCount / r.taskCount) * 100) : 0
  }

  get trabajo(): string | null {
    return durLabel(this.row?.workedMs ?? null)
  }

  get abierta(): string | null {
    return durLabel(this.row?.elapsedMs ?? null)
  }

  /**
   * Los días que la épica estuvo abierta sin que nadie la tocara. Solo se declara cuando pasa de
   * un día: por debajo de eso el hueco es ruido de redondeo entre tramos, no una pausa real.
   */
  get pausa(): string | null {
    const r = this.row
    if (r?.workedMs == null || r?.elapsedMs == null) return null
    const hueco = r.elapsedMs - r.workedMs
    return hueco >= MS_DAY ? durLabel(hueco) : null
  }

  /** Sigue abierta: hay tareas que no llegaron a Deploy. */
  get enVuelo(): boolean {
    return !!this.row?.startedAt && !this.row?.endedAt
  }

  // ─────────── Corregir la épica (F-037) ───────────

  //* Las reglas viven en `epic-actions` porque Métricas las necesita idénticas: una épica VACÍA no
  //* llega a esta ficha —el Gantt solo dibuja las que arrancaron— y es justo la que hay que retirar.
  get puedeRetirar(): boolean {
    return puedeRetirarEpica(this.row, this.isDev)
  }

  get motivoNoRetirar(): string | null {
    return motivoNoRetirarEpica(this.row, this.isDev)
  }

  empezarRenombrar(): void {
    if (!this.isDev || !this.row) return
    this.tituloBorrador = this.row.epic.title
    this.errorMsg = null
    this.editandoTitulo = true
  }

  cancelarRenombrar(): void {
    this.editandoTitulo = false
    this.errorMsg = null
  }

  guardarTitulo(): void {
    const r = this.row
    const titulo = this.tituloBorrador.trim()
    if (!r || this.guardando) return
    if (!titulo) {
      this.errorMsg = 'El nombre no puede quedar vacío.'
      return
    }
    //* Sin cambio no se llama al servidor: sería una entrada más en el historial de la épica que no
    //* dice nada.
    if (titulo === r.epic.title) {
      this.editandoTitulo = false
      return
    }
    this.guardando = true
    this.errorMsg = null
    this.worklog.updateEpic(r.epic._id, { title: titulo }).subscribe({
      next: () => {
        //* Se refleja en el acto sobre la fila que ya se tiene, sin esperar a que el contenedor
        //* recargue: el criterio es ver el cambio sin recargar la pantalla.
        r.epic.title = titulo
        this.guardando = false
        this.editandoTitulo = false
        this.changed.emit()
      },
      error: (err) => {
        this.guardando = false
        this.errorMsg = worklogErrorMessage(err, 'No se pudo renombrar la épica.')
      },
    })
  }

  pedirRetiro(): void {
    if (!this.puedeRetirar) return
    this.errorMsg = null
    this.confirmandoRetiro = true
  }

  cancelarRetiro(): void {
    this.confirmandoRetiro = false
  }

  retirar(): void {
    const r = this.row
    if (!r || this.guardando || !this.puedeRetirar) return
    this.guardando = true
    this.errorMsg = null
    this.worklog.removeEpic(r.epic._id).subscribe({
      next: () => {
        this.guardando = false
        this.confirmandoRetiro = false
        this.changed.emit()
        this.close()
      },
      error: (err) => {
        this.guardando = false
        this.confirmandoRetiro = false
        this.errorMsg = worklogErrorMessage(err, 'No se pudo retirar la épica.')
      },
    })
  }

  close(): void {
    this.visible = false
    this.visibleChange.emit(false)
  }
}
